'use client';

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
// BrowserPreview import removed

/* -------------------------------------------------------------------------------------------------
 * ASSETS (Using high-quality placeholders or generated assets would serve best here.
 * For now, I will use placeholder colors/gradients with overlay text/icons to represent the projects
 * cleanly until real screenshots are available.)
 * -----------------------------------------------------------------------------------------------*/

interface Project {
    id: number;
    title: string;
    category: string;
    desc: string;
    color: string; // Gradient or solid bg
    tags: string[];
    link: string;
    image?: string;
    imageFit?: "cover" | "contain" | "fill";
}

const PROJECTS: Project[] = [
    {
        id: 1,
        title: "Ak47 Sports",
        category: "E-Commerce",
        desc: "High-performance sports accessories platform.",
        color: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", // Deep Blue
        tags: ["Next.js", "Shopify", "Tailwind"],
        link: "https://ak47sports.com",
        image: "/projects/ak47-card-bg.png",
        imageFit: "cover"
    },
    {
        id: 2,
        title: "Pincode Hotels",
        category: "Hospitality",
        desc: "Hotel booking & convention hall event management.",
        color: "linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%)", // Violet
        tags: ["Booking Engine", "Calendar", "React"],
        link: "https://pincodehotels.com",
        image: "/projects/pincode-card-bg.png",
        imageFit: "cover"
    },
    {
        id: 3,
        title: "Point of Sale",
        category: "Retail System",
        desc: "In-store POS for seamless retail operations.",
        color: "linear-gradient(135deg, #0f172a 0%, #334155 100%)", // Slate Tech
        tags: ["Real-time", "Inventory", "Offline-first"],
        link: "https://pos.ak47sports.com/pos",
        image: "/projects/pos-card-bg.png",
        imageFit: "contain"
    },
    {
        id: 4,
        title: "Inventory Mgmt",
        category: "SaaS Product",
        desc: "Stock adjustment, barcode printing & logistics.",
        color: "linear-gradient(135deg, #047857 0%, #10b981 100%)", // Emerald
        tags: ["Barcodes", "Dashboard", "Analytics"],
        link: "https://pos.ak47sports.com/inventory",
        image: "/projects/inventory-card-bg.png",
        imageFit: "contain"
    },
    {
        id: 5,
        title: "Admin System",
        category: "Internal Tool",
        desc: "Centralized CMS for image & text content management.",
        color: "linear-gradient(135deg, #be123c 0%, #f43f5e 100%)", // Rose
        tags: ["CMS", "RBAC", "Media Library"],
        link: "https://pos.ak47sports.com/services",
        image: "/projects/admin-card-bg.png",
        imageFit: "contain"
    }
];

interface Carousel3DProps {
    cardWidth?: number;
    cardHeight?: number;
    gap?: number;
    scaleStep?: number;
    zStep?: number;
    perspective?: number;
}

export default function ProjectCarousel3D({
    cardWidth = 400, // Wider for deskstop
    cardHeight = 500, // Taller portrait style
    gap = 60,
    scaleStep = 0.12,
    zStep = 80,
    perspective = 800
}: Carousel3DProps) {

    const [index, setIndex] = useState(0);
    const [flippedIndex, setFlippedIndex] = useState<number | null>(null);
    // BrowserPreview state removed
    const total = PROJECTS.length;

    // Auto-play (Optional, but nice for showcase)
    // useEffect(() => {
    //     const timer = setInterval(() => {
    //         setIndex(prev => (prev + 1) % total);
    //     }, 5000);
    //     return () => clearInterval(timer);
    // }, [total]);

    const handlePrev = useCallback(() => {
        setIndex(i => (i - 1 + total) % total);
        setFlippedIndex(null); // Reset flip
    }, [total]);

    const handleNext = useCallback(() => {
        setIndex(i => (i + 1) % total);
        setFlippedIndex(null); // Reset flip
    }, [total]);

    // Keyboard Nav
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") handlePrev();
            else if (e.key === "ArrowRight") handleNext();
            else if (e.key === "Escape") setFlippedIndex(null);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handlePrev, handleNext]);

    // --- RENDER HELPERS ---
    const getCardStyle = (i: number) => {
        let rel = i - index;
        // Wrap logic
        if (rel < -Math.floor(total / 2)) rel += total;
        if (rel > Math.floor(total / 2)) rel -= total;

        const absRel = Math.abs(rel);
        const z = -absRel * zStep;
        const scale = 1 / (1 + absRel * scaleStep);
        const x = rel * gap;
        const opacity = rel === 0 ? 1 : Math.max(0.5, 0.8 - (0.15 * absRel));
        const y = 0; // Keep centered Y for cleanliness, or add slight arc if requested. 
        // User code had `rel * 60` for Y which makes a diagonal stack. 
        // I will stick to a flat wide stack which is more professional for web sliders.

        // Let's add a slight rotation Y for that "Cover Flow" feel if requested, 
        // but the user's code just did `rotateZ`. Let's stick to their Z-space logic but refine it.
        const rotateY = rel * -15; // Rotate inward slightly

        return {
            x, z, scale, opacity, rotateY,
            zIndex: 100 - absRel,
            pointerEvents: rel === 0 ? 'auto' : 'none'
        };
    };

    return (
        <div className="w-full h-[600px] flex items-center justify-center relative overflow-hidden"
            style={{ perspective: `${perspective}px` }}>

            {/* CARDS CONTAINER */}
            <div className="relative w-full h-full flex items-center justify-center transform-style-3d">
                {PROJECTS.map((project, i) => {
                    const style = getCardStyle(i);
                    const isActive = i === index;
                    const isFlipped = i === flippedIndex;

                    return (
                        <motion.div
                            key={project.id}
                            className={`absolute rounded-3xl shadow-2xl cursor-pointer
                                       ${isActive ? 'ring-1 ring-black/5' : ''}`}
                            initial={false}
                            animate={{
                                x: style.x,
                                z: style.z,
                                scale: style.scale,
                                opacity: style.opacity,
                                rotateY: style.rotateY + (isFlipped ? 180 : 0), // Add flip rotation
                                rotateZ: 0
                            }}
                            transition={{
                                duration: 0.6,
                                ease: [0.23, 1, 0.32, 1] // swift OutQuint
                            }}
                            style={{
                                width: cardWidth,
                                height: cardHeight,
                                zIndex: style.zIndex as any,
                                pointerEvents: style.pointerEvents as any,
                                boxShadow: "0 20px 60px -10px rgba(0,0,0,0.15), 0 10px 20px -5px rgba(0,0,0,0.1)",
                                transformStyle: "preserve-3d" // Critical for flipping
                            }}
                            onClick={(e) => {
                                if (!isActive) {
                                    setIndex(i);
                                    setFlippedIndex(null);
                                } else {
                                    // Active card clicked - Toggle flip or just do nothing (let buttons handle actions)
                                    // Originally opened preview. Now we just let it be.
                                    // Maybe toggle flip if clicking the card body?
                                    // For now, let's just do nothing on body click if already active, 
                                    // or maybe flip it. Let's flip it for better UX.
                                    setFlippedIndex(i === flippedIndex ? null : i);
                                }
                            }}
                        >
                            {/* =======================
                                FRONT FACE
                               ======================= */}
                            <div className="absolute inset-0 bg-white rounded-3xl overflow-hidden flex flex-col backface-hidden"
                                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>

                                {/* Visual Top Half */}
                                <div className="h-[55%] w-full relative p-6 flex flex-col justify-end overflow-hidden"
                                    style={{ background: project.color }}>

                                    {/* Background Image */}
                                    {project.image && (
                                        <Image
                                            src={project.image}
                                            alt={project.title}
                                            fill
                                            className="object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    )}

                                    {/* Overlay Gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

                                    {/* INFO BUTTON (Flip Trigger) */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFlippedIndex(i);
                                        }}
                                        className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-blue-600 transition-all"
                                        title="View Project Details"
                                    >
                                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </button>

                                    {/* Project Title overlay */}
                                    <div className="relative z-10 text-white">
                                        <span className="inline-block px-2 py-1 rounded-md bg-white/20 backdrop-blur-md text-[10px] font-bold uppercase tracking-wider mb-2 border border-white/10">
                                            {project.category}
                                        </span>
                                        <h3 className="text-3xl font-bold leading-tight">{project.title}</h3>
                                    </div>
                                </div>

                                {/* Details Bottom Half */}
                                <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {project.desc}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-4">
                                        {project.tags.map(tag => (
                                            <span key={tag} className="text-[10px] items-center px-2 py-1 rounded-full bg-slate-100 text-slate-500 font-medium">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Action Button - Converted to Link */}
                                    <a
                                        href={project.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between group cursor-pointer"
                                        onClick={(e) => {
                                            // Allow propagation ONLY if active, effectively handled by native a tag behavior
                                            // The parent onClick handles centering. 
                                            // This link should trigger native navigation.
                                            // We might need e.stopPropagation() if the parent onClick interferes.
                                            // But standard event bubbling means parent gets click too.
                                            // Center logic is: setIndex(i). If active (i===index), setIndex does nothing.
                                            // So native link behavior should proceed. 

                                            // HOWEVER, if the card is NOT active, we might want to prevent nav and just center?
                                            if (!isActive) {
                                                e.preventDefault();
                                                // Parent onClick will handle centering
                                            }
                                        }}
                                    >
                                        <span className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                                            Visit Live Site
                                        </span>
                                        <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
                                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </div>
                                    </a>
                                </div>
                            </div>

                            {/* =======================
                                BACK FACE (DETAILS)
                               ======================= */}
                            <div
                                className="absolute inset-0 bg-white rounded-3xl overflow-hidden flex flex-col p-8 backface-hidden"
                                style={{
                                    backfaceVisibility: 'hidden',
                                    WebkitBackfaceVisibility: 'hidden',
                                    transform: 'rotateY(180deg)' // Pre-rotate back face to match flip
                                }}
                            >
                                <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                                    <h4 className="text-lg font-bold text-slate-900">Project Details</h4>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFlippedIndex(null);
                                        }}
                                        className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-all"
                                    >
                                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>

                                <div className="space-y-6 overflow-y-auto pr-2 custom-scrollbar flex-1">
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">The Challenge</h5>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            To build a scalable, high-performance solution that integrates seamlessly with existing workflows while providing real-time data synchronization.
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Our Solution</h5>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            We engineered a custom architecture using Next.js and Node.js, delivering sub-second load times and offline capabilities for field operations.
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Key Tech</h5>
                                        <div className="flex flex-wrap gap-2">
                                            {project.tags.concat(['TypeScript', 'PostgreSQL', 'Redis']).map((t, idx) => (
                                                <span key={idx} className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-600 font-semibold border border-blue-100">{t}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </motion.div>
                    );
                })}
            </div>

            {/* NAVIGATION BUTTONS */}
            <button
                onClick={handlePrev}
                className="absolute left-4 md:left-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md shadow-lg border border-white flex items-center justify-center text-slate-700 hover:scale-110 active:scale-95 transition-all outline-none"
            >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button
                onClick={handleNext}
                className="absolute right-4 md:right-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md shadow-lg border border-white flex items-center justify-center text-slate-700 hover:scale-110 active:scale-95 transition-all outline-none"
            >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>

            {/* BROWSER PREVIEW REMOVED */}
        </div>
    );
}
